import React, { useState, useMemo } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useSubscriptionsStore } from '../../stores/useSubscriptionsStore';
import { Producto } from '../../types';
import { Cliente } from '../../types';

interface ModalAltaMembresiaProps {
  empresaId: string;
  subscriptionProducts: Producto[];
  clientes: Cliente[];
  onClose: () => void;
  onSuccess: () => void;
}

export function ModalAltaMembresia({
  empresaId,
  subscriptionProducts,
  clientes,
  onClose,
  onSuccess,
}: ModalAltaMembresiaProps) {
  const { createSubscription } = useSubscriptionsStore();
  const [clienteId, setClienteId] = useState('');
  const [productoId, setProductoId] = useState('');
  const [startDate, setStartDate] = useState(() =>
    new Date().toISOString().split('T')[0]
  );
  const [searchCliente, setSearchCliente] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const filteredClientes = useMemo(() => {
    if (!searchCliente.trim()) return clientes.slice(0, 100);
    const term = searchCliente.trim().toLowerCase();
    return clientes.filter((c) => {
      const name = [c.nombre, c.apellido].filter(Boolean).join(' ').toLowerCase();
      const tel = (c.telefono ?? '').toLowerCase();
      return name.includes(term) || tel.includes(term);
    }).slice(0, 50);
  }, [clientes, searchCliente]);

  const selectedProduct = subscriptionProducts.find((p) => p.id === productoId);
  const duracionDias = selectedProduct?.duracionDias ?? 30;
  const clasesIncluidas = selectedProduct?.clasesIncluidas ?? null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!clienteId || !productoId) {
      setError('Selecciona cliente y plan.');
      return;
    }

    setSaving(true);
    try {
      const today = new Date().toISOString().split('T')[0] ?? '';
      const start = (startDate && startDate.trim()) || today;
      const startDateObj = new Date(start);
      const endDateObj = new Date(startDateObj);
      endDateObj.setDate(endDateObj.getDate() + duracionDias);
      const periodEnd = endDateObj.toISOString().split('T')[0];

      const created = await createSubscription({
        empresa_id: empresaId,
        cliente_id: clienteId,
        producto_id: productoId,
        status: 'active',
        start_date: start,
        current_period_start: start,
        current_period_end: periodEnd,
        next_billing_date: periodEnd,
        classes_remaining: clasesIncluidas,
        total_classes_used: 0,
        auto_renew: selectedProduct?.autoRenovar !== false,
        cancelled_at: null,
        payment_method_id: null,
        metadata: {},
      });

      if (created) {
        onSuccess();
      } else {
        setError('No se pudo crear la membresía. Revisa los datos.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-gray-500/75" onClick={onClose} aria-hidden />
        <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Dar de alta membresía</h2>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 rounded-lg p-1"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {subscriptionProducts.length === 0 ? (
            <p className="text-gray-600 py-4">
              No hay planes de suscripción. Crea un producto en Productos y márcalo como suscripción/membresía.
            </p>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Cliente */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cliente
                </label>
                <input
                  type="text"
                  placeholder="Buscar por nombre o teléfono..."
                  value={searchCliente}
                  onChange={(e) => setSearchCliente(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <select
                  value={clienteId}
                  onChange={(e) => setClienteId(e.target.value)}
                  className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Selecciona un cliente</option>
                  {filteredClientes.map((c) => (
                    <option key={c.id} value={c.id}>
                      {[c.nombre, c.apellido].filter(Boolean).join(' ').trim() || 'Sin nombre'}
                      {c.telefono ? ` · ${c.telefono}` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Plan */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Plan / membresía
                </label>
                <select
                  value={productoId}
                  onChange={(e) => setProductoId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Selecciona un plan</option>
                  {subscriptionProducts.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nombre}
                      {p.frecuenciaCobro ? ` · ${p.frecuenciaCobro}` : ''}
                      {p.clasesIncluidas != null ? ` · ${p.clasesIncluidas} clases` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Fecha inicio */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fecha de inicio
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {error && (
                <p className="text-sm text-red-600">{error}</p>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? 'Guardando...' : 'Dar de alta'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
